import {Icon, Label, Loading} from '@therms/atalaya'
import {
  DateTime,
  ErrorLoadingRetry,
  FilesListDisplay,
  LatLngMapLink,
  LocationContextValue,
  useLocation,
  useLocationCheckpointScan,
  useLocationContext,
  UserName,
} from '@therms/react-modules'
import haversine from 'haversine-distance'
import {useMemo} from 'react'
import {MdNfc, MdOutlineQrCode} from 'react-icons/md'

interface CheckpointScanDetailsProps {
  checkpointScanId: string
}

function CheckpointScanDetails({checkpointScanId}: CheckpointScanDetailsProps) {
  const locationCtx: LocationContextValue | undefined = useLocationContext()

  const {data, loading, refresh} = useLocationCheckpointScan(
    {checkpointScanId},
    {cacheStrategy: 'cache-first'},
  )
  const {data: locationData} = useLocation(
    data?.checkpoint?.locationId && {
      locationId: data?.checkpoint?.locationId,
    },
    {fire: !locationCtx && !!data?.checkpoint?.locationId},
  )

  const scanGeoDistance = useMemo(() => {
    if (!data || !data.checkpointScan.scan?.geo?.coords) return null

    const checkpointCoords = data.checkpoint?.geo?.coordinates

    if (checkpointCoords) {
      return `${haversine(
        data.checkpointScan.scan.geo.coords,
        checkpointCoords,
      ).toFixed(0)}m from Checkpoint`
    }

    const coords =
      locationCtx?.location.geo?.coordinates ||
      locationData?.location.geo?.coordinates
    if (coords) {
      return `${haversine(data.checkpointScan.scan.geo.coords, coords).toFixed(
        0,
      )}m from Location`
    }

    return ''
  }, [data, locationData])

  if (loading && !data) return <Loading overlay />

  if (!data) return <ErrorLoadingRetry onRetry={refresh} />

  const {checkpoint, checkpointScan, entryType} = data

  return (
    <div>
      <div className="grid grid-cols-5">
        <Label>
          <span className="uppercase">
            <Icon
              className="mr-xs"
              Icon={checkpoint?.type === 'qr' ? MdOutlineQrCode : MdNfc}
            />
            <span className="font-light">{checkpoint?.type} </span>
          </span>
        </Label>

        <div className="col-span-4">{checkpoint?.name}</div>
      </div>

      <div className="grid grid-cols-5">
        <Label>Scan Time</Label>
        <div className="col-span-4">
          <DateTime timestamp={checkpointScan.scan.timestamp} />
        </div>
      </div>

      <div className="grid grid-cols-5">
        <Label>User</Label>
        <div className="col-span-4">
          <UserName userId={checkpointScan.scan.userId} />
        </div>
      </div>

      <div className="grid grid-cols-5">
        <Label>GPS</Label>
        <div className="col-span-4">
          {checkpointScan.scan.geo ? (
            <div>
              {scanGeoDistance || (
                <LatLngMapLink
                  lat={checkpointScan.scan.geo.coords.lat}
                  lng={checkpointScan.scan.geo.coords.lng}
                />
              )}
            </div>
          ) : (
            <span>No GPS data from device</span>
          )}
        </div>
      </div>

      {!!checkpointScan.scan.notes && (
        <div className="grid grid-cols-5">
          <Label>User Note</Label>
          <div className="col-span-4">{checkpointScan.scan.notes}</div>
        </div>
      )}

      {checkpointScan.scan.fileUrls?.length && (
        <div className="grid grid-cols-5">
          <Label>Files</Label>

          <FilesListDisplay fileUrls={checkpointScan.scan.fileUrls} />
        </div>
      )}

      {/* todo: show a link or the Location Log Entry - checkpointScan.scan.entryId */}
    </div>
  )
}

export {CheckpointScanDetails}
